import { css } from 'lit-element';
import { KEYCODE } from './keycode.js';
/**
 * An advanced version of the focus-blur mixin.
 *
 * Adds item-focused class to multiple shadow-root
 * elements with class focus-item on focusing
 *
 * Adds class on host component by default
 *
 * set this._setHostFocus = false to prevent
 * adding class to host.
 *
 * Also has support for enter key press where
 * the deriving component can override the
 * _enterKeyAction function to implement what
 * happens on pressing enter on the focus-item.
 */
export const MultiItemFocusBlurMixin = (superClass) =>
  class extends superClass {
    static get properties() {
      return {
        _setHostFocus: { type: Boolean, reflect: true, attribute: 'set-host-focus' },
      };
    }
    static get styles() {
      return css`
        :host([set-host-focus]:not(.item-focused)),
        .focus-item:not(.item-focused) {
          outline: none;
        }
        :host(.item-focused:not(.touch-device)),
        :host(:not(.touch-device)) *.item-focused {
          outline: var(--focus-outline) !important;
          outline-offset: -0.125rem;
        }
      `;
    }
    constructor() {
      super();
      this._setHostFocus = true;
    }

    connectedCallback() {
      super.connectedCallback();
      this._keyUpListener = this._keyUpCallBack.bind(this);
      this._shadowFocusOutListener = this._shadowFocusOutCallBack.bind(this);
      this.addEventListener('keyup', this._keyUpListener);
      this.shadowRoot.addEventListener('focusout', this._shadowFocusOutListener);
      if (this._setHostFocus) {
        this.focusOutListener = this.focusOutCallBack.bind(this);
        this.addEventListener('focusout', this.focusOutListener);
      }
    }
    disconnectedCallback() {
      this.removeEventListener('keyup', this._keyUpListener);
      this.shadowRoot.removeEventListener('focusout', this._shadowFocusOutListener);
      this.removeEventListener('focusout', this.focusOutListener);
      super.disconnectedCallback();
    }
    _keyUpCallBack(e) {
      const pressedEnter = e.keyCode === KEYCODE.ENTER;
      const pressedTab = e.keyCode === KEYCODE.TAB;
      const shadowRootActiveElement = this.shadowRoot.activeElement;
      let focusedElement;
      if (pressedEnter || pressedTab) {
        if (shadowRootActiveElement) {
          const focusItems = this.shadowRoot.querySelectorAll('.focus-item');
          focusedElement = Array.from(focusItems).find((el) => e.composedPath()[0] === el);
          this.classList.remove('item-focused');
        } else if (this._setHostFocus && e.composedPath()[0] === this) {
          focusedElement = this;
        }
        if (focusedElement && pressedEnter) {
          this._enterKeyAction(focusedElement);
        } else if (focusedElement && pressedTab) {
          focusedElement.classList.add('item-focused');
        }
      }
    }
    _shadowFocusOutCallBack() {
      const focusedItem = this.shadowRoot.querySelector('.item-focused');
      if (focusedItem) {
        focusedItem.classList.remove('item-focused');
      }
    }
    focusOutCallBack() {
      this.classList.remove('item-focused');
    }
    _enterKeyAction(focusedElement) {
      if (focusedElement) return;
    }
  };
